/**
 * Подписка на события WebSocket-канала.
 *
 * Каналов два: партии (`/ws/session/{id}`) и комнаты (`/ws/room/{id}`). Комната
 * живёт дольше партии, поэтому канал у неё свой — сокет партии закрывается
 * вместе с ней. Механика подключения одна и та же, отличается только путь.
 *
 * Socket.io намеренно не используется: на стороне Go проще и надёжнее нативный
 * WebSocket, а из клиентских удобств нужен только автоматический реконнект.
 */
import { useEffect, useRef, useState } from "react";

import { API_URL, getAccessToken } from "../api/client";
import type { SessionEvent } from "./events";

type ConnectionState = "connecting" | "open" | "closed";

interface Options {
  /** Пока сессия не создана, подключаться незачем. */
  enabled?: boolean;
  onEvent?: (event: SessionEvent) => void;
}

const MAX_RECONNECT_DELAY = 10_000;

export function useSessionSocket(sessionId: string | undefined, options: Options = {}) {
  return useChannelSocket("session", sessionId, options);
}

/**
 * Подписка на канал комнаты.
 *
 * Держится открытой на любой странице: через неё приходит сигнал о начале
 * партии, и участник должен узнать о нём, где бы он ни находился. Она же
 * удерживает его присутствие в комнате — по одному соединению на вкладку.
 */
export function useRoomSocket(roomId: string | undefined, options: Options = {}) {
  return useChannelSocket("room", roomId, options);
}

function useChannelSocket(
  channel: "session" | "room",
  id: string | undefined,
  options: Options = {},
) {
  const { enabled = true, onEvent } = options;
  const [state, setState] = useState<ConnectionState>("closed");

  // Колбэк держим в ref, чтобы смена обработчика не пересоздавала соединение.
  const handlerRef = useRef(onEvent);
  useEffect(() => {
    handlerRef.current = onEvent;
  }, [onEvent]);

  useEffect(() => {
    if (!id || !enabled) return;

    let socket: WebSocket | null = null;
    let reconnectTimer: number | undefined;
    let attempt = 0;
    let disposed = false;

    const connect = () => {
      const token = getAccessToken();
      if (!token) {
        // Токен ещё не восстановлен после перезагрузки страницы — подождём.
        reconnectTimer = window.setTimeout(connect, 500);
        return;
      }

      // Браузерный WebSocket не позволяет задать заголовок Authorization,
      // поэтому токен передаётся query-параметром.
      const url = new URL(`${API_URL}/ws/${channel}/${id}`);
      url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
      url.searchParams.set("token", token);

      setState("connecting");
      socket = new WebSocket(url.toString());

      socket.onopen = () => {
        attempt = 0;
        setState("open");
      };

      socket.onmessage = (message) => {
        try {
          handlerRef.current?.(JSON.parse(message.data as string) as SessionEvent);
        } catch {
          // Нечитаемое сообщение не должно рвать соединение.
        }
      };

      socket.onclose = () => {
        setState("closed");
        if (disposed) return;

        // Экспоненциальная задержка с потолком: при падении backend'а вкладка
        // не должна долбить сервер каждые 100 мс.
        attempt += 1;
        const delay = Math.min(500 * 2 ** (attempt - 1), MAX_RECONNECT_DELAY);
        reconnectTimer = window.setTimeout(connect, delay);
      };

      socket.onerror = () => socket?.close();
    };

    connect();

    return () => {
      disposed = true;
      window.clearTimeout(reconnectTimer);
      socket?.close();
    };
  }, [channel, id, enabled]);

  return { state };
}
